import React from "react";
import { Link, useParams } from "react-router-dom";
import {
  Box,
  Chip,
  Divider,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import {
  AttachMoneyOutlined,
  CalendarTodayOutlined,
  DescriptionOutlined,
  InfoOutlined,
  LocalShippingOutlined,
  PaymentOutlined,
  PersonOutlined,
  ReceiptOutlined,
  BusinessOutlined,
} from "@mui/icons-material";

import InformationTile from "components/Shared/InformationTile";
import InfoGridSkeleton from "components/Shared/Skeleton/InfoGridSkeleton";
import { localizeDateTimeShort } from "utils/dateFormats";
import { useGetShipmentRefundDetailQuery } from "store/services/PaymentsApi/shipmentRefundApi";

const statusStyles = {
  Approved: { backgroundColor: "#EBF5FB", textColor: "green" },
  Pending: { backgroundColor: "#FEF9E7", textColor: "#D4AC0D" },
  Rejected: { backgroundColor: "#FADBD8", textColor: "#C0392B" },
  default: { backgroundColor: "#E9ECEF", textColor: "#6C757D" },
};

const getStatusStyle = (status) => statusStyles[status] || statusStyles.default;

const formatCurrency = (value) => {
  if (value == null || Number.isNaN(Number(value))) return "N/A";
  return `Rs. ${Number(value).toFixed(2)}`;
};

const ShipmentRefundTab = ({ shipmentId: shipmentIdProp }) => {
  const { id: routeId } = useParams();
  const shipmentId = shipmentIdProp || routeId;

  const {
    data: shipmentRefundDetail,
    isLoading,
    isFetching,
  } = useGetShipmentRefundDetailQuery(shipmentId, {
    skip: !shipmentId,
  });

  const shipmentTotalCharge = Array.isArray(shipmentRefundDetail?.shipmentPrice)
    ? shipmentRefundDetail.shipmentPrice.reduce(
        (sum, price) => sum + (Number(price?.charge) || 0),
        0,
      )
    : 0;

  const financeStatus = shipmentRefundDetail?.financeReview?.status;
  const isApproved = financeStatus === "Approved";
  const isRejected = financeStatus === "Rejected";
  const financeActor = isApproved
    ? shipmentRefundDetail?.financeReview?.approvedBy?.name
    : shipmentRefundDetail?.financeReview?.rejectedBy?.name;

  if (isLoading || isFetching) {
    return <InfoGridSkeleton />;
  }

  if (!shipmentRefundDetail) {
    return (
      <Box px={3} py={4}>
        <Typography variant="h7" sx={{color: "primary.main"}}>
         <strong>Note: </strong> This shipment does not have any refund information.
        </Typography>
      </Box>
    );
  }

  return (
    <Box px={3} py={3}>
      <Stack spacing={1.5} mb={3}>
        <Typography variant="h6">Refund</Typography>
      </Stack>

      <Grid container spacing={3}>
        <Grid item xs={12} md={3}>
          <InformationTile
            icon={() => <ReceiptOutlined color="primary" />}
            title="Refund ID"
            subtitle={
              shipmentRefundDetail?._id ? (
                <Link
                  to={`/app/crm/shipment-refund/${shipmentRefundDetail._id}/detail`}
                  style={{ color: "inherit", textDecoration: "underline" }}
                >
                  {shipmentRefundDetail?.refundId || "N/A"}
                </Link>
              ) : (
                "N/A"
              )
            }
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <InformationTile
            icon={() => <LocalShippingOutlined color="primary" />}
            title="Shipment"
            subtitle={shipmentRefundDetail?.shipment?.shipmentId || "N/A"}
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <InformationTile
            icon={() => <AttachMoneyOutlined color="primary" />}
            title="Shipment Total Charge"
            subtitle={formatCurrency(shipmentTotalCharge)}
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <InformationTile
            icon={() => <AttachMoneyOutlined color="primary" />}
            title="Refund Amount"
            subtitle={formatCurrency(shipmentRefundDetail?.amount)}
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <InformationTile
            icon={() => <DescriptionOutlined color="primary" />}
            title="Refund Type"
            subtitle={shipmentRefundDetail?.refundType?.name || "N/A"}
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <InformationTile
            icon={() => <PaymentOutlined color="primary" />}
            title="Refund Method"
            subtitle={shipmentRefundDetail?.refundMethod || "N/A"}
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <InformationTile
            icon={() => <CalendarTodayOutlined color="primary" />}
            title="Refund Date"
            subtitle={
              shipmentRefundDetail?.refundDate
                ? localizeDateTimeShort(shipmentRefundDetail.refundDate)
                : "N/A"
            }
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <InformationTile
            icon={() => <PersonOutlined color="primary" />}
            title={isApproved ? "Approved By" : isRejected ? "Rejected By" : "Reviewed By"}
            subtitle={financeActor || "N/A"}
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <InformationTile
            icon={() => <PaymentOutlined color="primary" />}
            title="Finance Payment Method"
            subtitle={shipmentRefundDetail?.financeReview?.paymentMethod?.name || "N/A"}
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <InformationTile
            icon={() => <CalendarTodayOutlined color="primary" />}
            title="Finance Approved Date"
            subtitle={
              shipmentRefundDetail?.financeReview?.paymentDate
                ? localizeDateTimeShort(
                    shipmentRefundDetail.financeReview.paymentDate,
                  )
                : "N/A"
            }
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <InformationTile
            icon={() => <InfoOutlined color="primary" />}
            title="Status"
            subtitle={
              <Chip
                label={shipmentRefundDetail?.refundStatus || "Unknown"}
                size="small"
                sx={{
                  mt: 0.5,
                  backgroundColor: getStatusStyle(
                    shipmentRefundDetail?.refundStatus,
                  ).backgroundColor,
                  color: getStatusStyle(shipmentRefundDetail?.refundStatus)
                    .textColor,
                  fontWeight: 700,
                  textTransform: "uppercase",
                }}
              />
            }
          />
        </Grid>

        {shipmentRefundDetail?.financeReview?.bankName?.name && (
          <Grid item xs={12} md={3}>
            <InformationTile
              icon={() => <BusinessOutlined color="primary" />}
              title="Bank Name"
              subtitle={shipmentRefundDetail.financeReview.bankName.name}
            />
          </Grid>
        )}

        {shipmentRefundDetail?.refundReason && (
          <Grid item xs={12} md={6}>
            <InformationTile
              icon={() => <DescriptionOutlined color="primary" />}
              title="Refund Reason"
              subtitle={shipmentRefundDetail.refundReason}
            />
          </Grid>
        )}

        {shipmentRefundDetail?.rejectReason && (
          <Grid item xs={12} md={6}>
            <InformationTile
              icon={() => <DescriptionOutlined color="error" />}
              title="Reject Reason"
              subtitle={shipmentRefundDetail.rejectReason}
            />
          </Grid>
        )}

        {shipmentRefundDetail?.financeReview?.remarks && (
          <Grid item xs={12}>
            <InformationTile
              icon={() => <DescriptionOutlined color="primary" />}
              title="Finance Remarks"
              subtitle={shipmentRefundDetail.financeReview.remarks}
            />
          </Grid>
        )}
      </Grid>

      <Divider sx={{ my: 3 }} />

      <Typography variant="body2" color="text.secondary">
        Refund status: {shipmentRefundDetail?.refundStatus || "Unknown"}
      </Typography>
    </Box>
  );
};

export default ShipmentRefundTab;
